import React, { useState, useMemo } from 'react';
import { FlashcardConfig, HistoryEntry, UserAnswer, Question } from '../types';
import Flashcard from './Flashcard';

interface FlashcardScreenProps {
    config: FlashcardConfig;
    history: HistoryEntry[];
    onExit: () => void;
    allQuestions: Question[];
    displayLanguage: 'pt' | 'en' | 'es';
}

const FlashcardScreen: React.FC<FlashcardScreenProps> = ({ config, history, onExit, allQuestions, displayLanguage }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    };

    const filteredQuestions = useMemo(() => {
        let questions = allQuestions;

        if (config.onlyIncorrect && history.length > 0) {
            const incorrectQuestionIds = new Set<number>();
            history.forEach(entry => {
                entry.answers.forEach((answer: UserAnswer, questionId: number) => {
                    if (!answer.isCorrect) {
                        incorrectQuestionIds.add(questionId);
                    }
                });
            });
            questions = questions.filter(q => incorrectQuestionIds.has(q.id));
        }

        if (config.domains.length > 0) {
            const filteredDomains = new Set(config.domains);
            questions = questions.filter(q => q.domain && filteredDomains.has(q.domain));
        }

        return shuffleArray(questions);
    }, [config, history, allQuestions]);

    const handleNext = () => {
        if (currentIndex < filteredQuestions.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setIsFlipped(false);
        }
    };
    
    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
            setIsFlipped(false);
        }
    };

    if (filteredQuestions.length === 0) {
        return (
            <div className="bg-white dark:bg-slate-800 p-8 rounded-lg shadow-2xl text-center flex flex-col items-center animate-fade-in">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Nenhuma Questão Encontrada</h2>
                <p className="text-gray-600 dark:text-slate-400 mb-6">Nenhuma questão corresponde aos filtros selecionados. Tente ajustar suas seleções.</p>
                <button
                    onClick={onExit}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-8 rounded-lg transition-transform duration-200 transform hover:scale-105 shadow-lg"
                >
                    <i className="fas fa-arrow-left mr-2"></i>
                    Voltar ao Início
                </button>
            </div>
        );
    }

    return (
        <div className="w-full animate-fade-in flex flex-col h-full">
            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-lg mb-6 sticky top-4 z-10">
                <div className="flex justify-between items-center">
                    <button onClick={onExit} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                        <i className="fas fa-times mr-2"></i>
                        Sair do Modo de Estudo
                    </button>
                    <div className="font-mono text-lg font-bold">
                        Flashcard {currentIndex + 1}/{filteredQuestions.length}
                    </div>
                </div>
            </div>
            
            <div className="flex-grow flex items-center justify-center py-4">
                <Flashcard 
                    question={filteredQuestions[currentIndex]}
                    isFlipped={isFlipped}
                    onFlip={() => setIsFlipped(!isFlipped)}
                    displayLanguage={displayLanguage}
                />
            </div>

            <div className="flex justify-between items-center mt-auto p-4 bg-white dark:bg-slate-800 rounded-lg shadow-lg sticky bottom-4 z-10 gap-4">
                <button
                    onClick={handlePrev}
                    disabled={currentIndex === 0}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 dark:bg-slate-600 dark:hover:bg-slate-500 dark:text-white font-bold py-2 px-6 rounded-lg transition-colors disabled:bg-gray-100 disabled:text-gray-400 dark:disabled:bg-slate-700 dark:disabled:text-slate-500 disabled:cursor-not-allowed flex-1"
                >
                    <i className="fas fa-arrow-left mr-2"></i>
                    Anterior
                </button>
                 <button
                    onClick={() => setIsFlipped(!isFlipped)}
                    className="bg-teal-500 hover:bg-teal-600 text-white font-bold py-2 px-6 rounded-lg transition-colors flex-1"
                >
                    {isFlipped ? 'Ocultar' : 'Revelar'}
                    <i className={`fas ${isFlipped ? 'fa-eye-slash' : 'fa-eye'} ml-2`}></i>
                </button>
                <button
                    onClick={handleNext}
                    disabled={currentIndex === filteredQuestions.length - 1}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-6 rounded-lg transition-colors disabled:bg-gray-100 disabled:text-gray-400 dark:disabled:bg-slate-700 dark:disabled:text-slate-500 disabled:cursor-not-allowed flex-1"
                >
                    Próxima
                    <i className="fas fa-arrow-right ml-2"></i>
                </button>
            </div>
        </div>
    );
};

export default FlashcardScreen;